import { getCookie } from '@alexis/helpers/cookie';
import { numberOfNights } from '@alexis/helpers/date';
import { isEnvironmentProduction } from '@alexis/helpers/environment';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useFlightContext } from '@context/FlightContext';

import {
  addSlotEventListener,
  loadGPTAds,
  refreshGPTAds,
  removeSlotEventListener,
  showGPTAds,
  unloadGPTAds,
} from '@helpers/ads';

import { useAdditionalGPTTargeting } from '@hooks/useGPTAdsTargeting';
import { useViewportSize } from '@hooks/useViewportSize';

import {
  getCurrencyState,
  getCurrentSiteState,
  getLocaleState,
  getUserState,
} from '@redux/selectors';

import { AdsTargeting } from '@wegoTypes/partnerSuccess/adsTargeting';

const useFlightSearchAds = () => {
  const { search, exchangeRate } = useFlightSearchResults();
  const { isShowNativeAd, isShowingJWPAds, isLazyloadAdsVariantA } = useFlightContext();

  const { width } = useViewportSize();
  const isScreenBetween1204And1343 = width >= 1204 && width <= 1343;
  const isScreenAbove1343 = width > 1343;

  const currentSite = useSelector(getCurrentSiteState);
  const user = useSelector(getUserState);
  const currency = useSelector(getCurrencyState);
  const locale = useSelector(getLocaleState);

  //#region GPT ads state
  const [googleSlots, setGoogleSlots] = useState<Array<googletag.Slot>>();
  const [topLeaderboardAdsHeight, setTopLeaderboardAdsHeight] = useState<number>(0);
  const [middleLeaderboardAdsHeight, setMiddleLeaderboardAdsHeight] = useState<number>(0);
  const [bottomLeaderboardAdsHeight, setBottomLeaderboardAdsHeight] = useState<number>(0);
  const [isNativeTopAdRendered, setIsNativeTopAdRendered] = useState<boolean>(false);
  const [isNativeMiddleAdRendered, setIsNativeMiddleAdRendered] = useState<boolean>(false);
  const [isNativeBottomAdRendered, setIsNativeBottomAdRendered] = useState<boolean>(false);
  const [nativeTopAdHeight, setNativeTopAdHeight] = useState<number | undefined>(undefined);
  const [nativeMiddleAdHeight, setNativeMiddleAdHeight] = useState<number | undefined>(undefined);
  const [nativeBottomAdHeight, setNativeBottomAdHeight] = useState<number | undefined>(undefined);
  const [topStickyAdHeight, setTopStickyAdHeight] = useState<number | undefined>(undefined);
  const [hasMrecOrTopStickyAds, setHasMrecOrTopStickyAds] = useState<boolean>(false);
  const [additionalTargetingParams, setAdditionalTargetingParams] = useState<
    AdsTargeting | undefined
  >(undefined);
  //#endregion

  useAdditionalGPTTargeting(
    {
      pageName: 'FLIGHT_SEARCH_RESULT',
      searchId: search?.id || '',
      wegoAnalyticsClientId: getCookie('wego_analytics_client_id') || '',
    },
    {
      enabled: !!search?.id && !!getCookie('wego_analytics_client_id'),
      onSuccessCallback: (data) => {
        setAdditionalTargetingParams(data);
      },
      onErrorCallback: () => {
        // set fallback
        setAdditionalTargetingParams({
          targeting: {},
        });
      },
    },
  );

  const baseTargetParams: { [key: string]: string } | undefined = useMemo(() => {
    if (search) {
      const hostname = window.location.hostname;
      const flightSearchResultSearch = search;

      const outboundLeg = flightSearchResultSearch.legs[0];
      const outboundDate = new Date(outboundLeg.outboundDate);
      const inboundLeg: FlightSearchResultSearchLeg | undefined = flightSearchResultSearch.legs[1];

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const leadtimeDays = numberOfNights(today.getTime(), outboundDate.getTime());

      return {
        _cctld: currentSite.countryCode,
        _product: 'flights',
        _locale: locale,
        _env: isEnvironmentProduction(hostname) ? 'production' : 'staging',
        _logged_in: user ? 'true' : 'false',
        _page_type: 'search results',
        _is_new: `${!getCookie('wego_visits_cnt')}`,

        _cabin: flightSearchResultSearch.cabin,
        _has_child: `${flightSearchResultSearch.childrenCount !== 0}`,
        _has_infant: `${flightSearchResultSearch.infantsCount !== 0}`,
        _dep_date: outboundLeg.outboundDate,
        _ret_date: inboundLeg !== undefined ? inboundLeg.outboundDate : '',
        _trip_type: inboundLeg ? 'roundtrip' : 'oneway',
        _duration:
          inboundLeg !== undefined
            ? numberOfNights(
                outboundDate.getTime(),
                new Date(inboundLeg.outboundDate).getTime(),
              ).toString()
            : '',
        _adults: flightSearchResultSearch.adultsCount.toString(),
        _children: flightSearchResultSearch.childrenCount.toString(),
        _infants: flightSearchResultSearch.infantsCount.toString(),
        _guests: (
          flightSearchResultSearch.adultsCount +
          flightSearchResultSearch.childrenCount +
          flightSearchResultSearch.infantsCount
        ).toString(),
        _domestic: `${
          outboundLeg.departureCity.countryEnName === outboundLeg.arrivalCity.countryEnName
        }`,
        _lead_time: Math.min(leadtimeDays, 365).toString(),
        _dep_date_month: outboundDate.toLocaleString('default', { month: 'long' }).toLowerCase(),
        _currency: currency.code,
        _currency_symbol: currency.symbol,
        _currency_conversion: (1 / exchangeRate.rate).toString(),

        _frm_acode: outboundLeg.departureCity.code,
        _frm_city: outboundLeg.departureCity.enName.toLowerCase(),
        _frm_city_locale: outboundLeg.departureCity.name.toLowerCase(),
        _frm_ctry: outboundLeg.departureCity.countryEnName.toLowerCase(),

        _to_acode: outboundLeg.arrivalCity.code,
        _to_city: outboundLeg.arrivalCity.enName.toLowerCase(),
        _to_city_locale: outboundLeg.arrivalCity.name.toLowerCase(),
        _to_ctry: outboundLeg.arrivalCity.countryEnName.toLowerCase(),
      };
    }
  }, [
    currency.code,
    currency.symbol,
    currentSite.countryCode,
    exchangeRate.rate,
    search?.id,
    locale,
    user,
  ]);

  const targetParams = useMemo(() => {
    const shouldWaitAdditionalTargeting = !additionalTargetingParams;
    if (shouldWaitAdditionalTargeting) {
      // making it undefined to avoid requesting ads before targeting data is loaded
      return undefined;
    }

    return {
      ...baseTargetParams,
      ...additionalTargetingParams?.targeting,
    };
  }, [additionalTargetingParams, baseTargetParams]);

  const initializeAds = async (
    slotsConfigurations: Array<{
      adUnitPath: string;
      size: googletag.GeneralSize;
      htmlDivId: string;
      targetParams: {
        [key: string]: string;
      };
    }>,
    isLazyloadAdsVariantA: boolean,
  ): Promise<void> => {
    const slots = await loadGPTAds(
      slotsConfigurations,
      true,
      isLazyloadAdsVariantA,
      !isLazyloadAdsVariantA,
      true,
      isLazyloadAdsVariantA
        ? {
            fetchMarginPercent: 30,
            renderMarginPercent: 30,
            mobileScaling: 1,
          }
        : {
            fetchMarginPercent: 500,
            renderMarginPercent: 10,
            mobileScaling: 1,
          },
    );

    addSlotEventListener('slotRenderEnded', handleSlotRenderEnded);

    setGoogleSlots(slots);
  };

  const handleSlotRenderEnded = (event: googletag.events.SlotRenderEndedEvent): void => {
    const slotElementId = event.slot.getSlotElementId();
    const size = event.size;

    if (
      (slotElementId === 'top_leaderboard' || slotElementId === 'dynamic_leaderboard_desktop') &&
      size !== null
    ) {
      setTopLeaderboardAdsHeight((size as Array<number>)[1]);
    }

    if (slotElementId === 'middle_leaderboard' && size !== null) {
      setMiddleLeaderboardAdsHeight((size as Array<number>)[1]);
    }

    if (slotElementId === 'bottom_leaderboard' && size !== null) {
      setBottomLeaderboardAdsHeight((size as Array<number>)[1]);
    }

    if (slotElementId === 'native_top_ad') {
      setIsNativeTopAdRendered(true);
    }

    if (slotElementId === 'native_middle_ad') {
      setIsNativeMiddleAdRendered(true);
    }
    if (slotElementId === 'native_bottom_ad') {
      setIsNativeBottomAdRendered(true);
    }

    if (/^(?:mrec_1|mrec_2|sticky_top)$/.test(slotElementId) && size !== null) {
      setHasMrecOrTopStickyAds(true);
    }

    if (slotElementId === 'sticky_top') {
      if (size !== null) {
        setTopStickyAdHeight((size as Array<number>)[1]);
      } else {
        // if no top sticky ad, set TopStickyAdHeight to 0
        setTopStickyAdHeight(0);
      }
    }
  };

  // Refresh GPT ads
  useEffect(() => {
    if (googleSlots) {
      if (isLazyloadAdsVariantA) {
        googleSlots.map((slot) => {
          showGPTAds(slot);
        });
      }
      refreshGPTAds(googleSlots);
    }
  }, [googleSlots, isLazyloadAdsVariantA]);

  // Initialize GPT Ads
  useEffect(() => {
    if (targetParams) {
      const slotsConfigurations: {
        adUnitPath: string;
        size: googletag.GeneralSize;
        htmlDivId: string;
        targetParams: {
          [key: string]: string;
        };
      }[] = isShowNativeAd
        ? [
            {
              adUnitPath: isLazyloadAdsVariantA
                ? '/1033625/desktop_native_flights_1_B_LL'
                : '/1033625/desktop_native_flights_1',
              size: [[728, 90], 'fluid'],
              htmlDivId: 'native_top_ad',
              targetParams,
            },
            {
              adUnitPath: isLazyloadAdsVariantA
                ? '/1033625/desktop_native_flights_2_B_LL'
                : '/1033625/desktop_native_flights_2',
              size: [[728, 90], 'fluid'],
              htmlDivId: 'native_middle_ad',
              targetParams,
            },
            {
              adUnitPath: isLazyloadAdsVariantA
                ? '/1033625/desktop_native_flights_3_B_LL'
                : '/1033625/desktop_native_flights_3',
              size: [[728, 90], 'fluid'],
              htmlDivId: 'native_bottom_ad',
              targetParams,
            },
          ]
        : [
            {
              adUnitPath: isLazyloadAdsVariantA
                ? '/1033625/desktop_leaderboard_1_B'
                : `/1033625/top_leaderboard`,
              size: [[728, 90]],
              htmlDivId: 'top_leaderboard',
              targetParams,
            },
            {
              adUnitPath: isLazyloadAdsVariantA
                ? '/1033625/desktop_leaderboard_2_B'
                : '/1033625/middle_leaderboard',
              size: [[728, 90]],
              htmlDivId: 'middle_leaderboard',
              targetParams,
            },
            {
              adUnitPath: isLazyloadAdsVariantA
                ? '/1033625/desktop_leaderboard_3_B'
                : '/1033625/bottom_leaderboard',
              size: [[728, 90]],
              htmlDivId: 'bottom_leaderboard',
              targetParams,
            },
          ];

      if (isScreenBetween1204And1343) {
        slotsConfigurations.push({
          adUnitPath: isLazyloadAdsVariantA ? '/1033625/skyscraper_B' : '/1033625/skyscraper',
          size: [[160, 600]],
          htmlDivId: 'skyscraper',
          targetParams,
        });
      }

      if (isScreenAbove1343) {
        slotsConfigurations.push(
          {
            adUnitPath: isLazyloadAdsVariantA ? '/1033625/mrec_1_B' : '/1033625/mrec_1',
            size: [
              [300, 250],
              [300, 600],
              [160, 600],
            ],
            htmlDivId: 'mrec_1',
            targetParams,
          },
          {
            adUnitPath: isLazyloadAdsVariantA ? '/1033625/mrec_2_B' : '/1033625/mrec_2',
            size: [[300, 250]],
            htmlDivId: 'mrec_2',
            targetParams,
          },
          {
            adUnitPath: isLazyloadAdsVariantA
              ? '/1033625/desktop_sticky_ads_B'
              : '/1033625/desktop_sticky_ads',
            size: [
              [300, 250],
              [300, 600],
            ],
            htmlDivId: 'sticky_top',
            targetParams,
          },
        );

        if (isShowingJWPAds) {
          slotsConfigurations.push({
            adUnitPath: '/1033625/JW_Player',
            size: [[300, 250]],
            htmlDivId: 'jwp_container',
            targetParams,
          });
        }
      }

      initializeAds(slotsConfigurations, isLazyloadAdsVariantA);
    }
    return () => {
      setGoogleSlots(undefined);
      setHasMrecOrTopStickyAds(false);
      setTopLeaderboardAdsHeight(0);
      setMiddleLeaderboardAdsHeight(0);
      setBottomLeaderboardAdsHeight(0);
      setTopStickyAdHeight(undefined);
      removeSlotEventListener('slotRenderEnded', handleSlotRenderEnded);
      unloadGPTAds();
    };
  }, [
    search?.id,
    isScreenAbove1343,
    isScreenBetween1204And1343,
    isShowNativeAd,
    targetParams,
    isLazyloadAdsVariantA,
    isShowingJWPAds,
  ]);

  //If native ads are not available, fetch and display leaderboard ads
  useEffect(() => {
    if (
      !isLazyloadAdsVariantA &&
      isShowNativeAd &&
      targetParams &&
      nativeTopAdHeight !== undefined &&
      nativeMiddleAdHeight !== undefined &&
      nativeBottomAdHeight !== undefined &&
      (nativeTopAdHeight === 0 || nativeMiddleAdHeight === 0 || nativeBottomAdHeight === 0)
    ) {
      const slotsConfigurations: {
        adUnitPath: string;
        size: googletag.GeneralSize;
        htmlDivId: string;
        targetParams: {
          [key: string]: string;
        };
      }[] = [];

      if (nativeTopAdHeight === 0) {
        slotsConfigurations.push({
          adUnitPath: `/1033625/top_leaderboard`,
          size: [[728, 90]],
          htmlDivId: 'top_leaderboard',
          targetParams,
        });
      }

      if (nativeMiddleAdHeight === 0) {
        slotsConfigurations.push({
          adUnitPath: '/1033625/middle_leaderboard',
          size: [[728, 90]],
          htmlDivId: 'middle_leaderboard',
          targetParams,
        });
      }

      if (nativeBottomAdHeight === 0) {
        slotsConfigurations.push({
          adUnitPath: '/1033625/bottom_leaderboard',
          size: [[728, 90]],
          htmlDivId: 'bottom_leaderboard',
          targetParams,
        });
      }

      initializeAds(slotsConfigurations, isLazyloadAdsVariantA);
    }
  }, [
    isLazyloadAdsVariantA,
    isShowNativeAd,
    nativeTopAdHeight,
    nativeBottomAdHeight,
    nativeMiddleAdHeight,
    targetParams,
  ]);

  // If Native ads are not received, call the leaderboard ads and is With lazy loading
  useEffect(() => {
    if (isLazyloadAdsVariantA && isShowNativeAd && targetParams && nativeTopAdHeight === 0) {
      initializeAds(
        [
          {
            adUnitPath: '/1033625/desktop_leaderboard_1_B',
            size: [[728, 90]],
            htmlDivId: 'top_leaderboard',
            targetParams,
          },
        ],
        isLazyloadAdsVariantA,
      );
    }
  }, [isLazyloadAdsVariantA, isShowNativeAd, nativeTopAdHeight, targetParams]);

  useEffect(() => {
    if (isLazyloadAdsVariantA && isShowNativeAd && targetParams && nativeMiddleAdHeight === 0) {
      initializeAds(
        [
          {
            adUnitPath: '/1033625/desktop_leaderboard_2_B',
            size: [[728, 90]],
            htmlDivId: 'middle_leaderboard',
            targetParams,
          },
        ],
        isLazyloadAdsVariantA,
      );
    }
  }, [isLazyloadAdsVariantA, isShowNativeAd, nativeMiddleAdHeight, targetParams]);

  useEffect(() => {
    if (isLazyloadAdsVariantA && isShowNativeAd && targetParams && nativeBottomAdHeight === 0) {
      initializeAds(
        [
          {
            adUnitPath: '/1033625/desktop_leaderboard_3_B',
            size: [[728, 90]],
            htmlDivId: 'bottom_leaderboard',
            targetParams,
          },
        ],
        isLazyloadAdsVariantA,
      );
    }
  }, [isLazyloadAdsVariantA, isShowNativeAd, nativeBottomAdHeight, targetParams]);

  //Fetch and display bottom sticky ad only when top sticky ad is 250 height or don't exist
  useEffect(() => {
    if (
      targetParams &&
      isScreenAbove1343 &&
      (topStickyAdHeight === 250 || topStickyAdHeight === 0)
    ) {
      const initializeAds = async (): Promise<void> => {
        const slotsConfigurations: {
          adUnitPath: string;
          size: googletag.GeneralSize;
          htmlDivId: string;
          targetParams: {
            [key: string]: string;
          };
        }[] = [
          {
            adUnitPath: '/1033625/desktop_sticky_ads_bottom',
            size: [[300, 250]],
            htmlDivId: 'sticky_bottom',
            targetParams,
          },
        ];

        const slots = await loadGPTAds(slotsConfigurations, true, false, true, true, {
          fetchMarginPercent: 500,
          renderMarginPercent: 10,
          mobileScaling: 1,
        });

        refreshGPTAds(slots);
      };

      initializeAds();
    }
  }, [search?.id, isScreenAbove1343, targetParams, topStickyAdHeight]);

  // As native ads size can be fluid, size returned from google will be [0,0] so need to get height after fluid ads become visible
  // More details https://developers.google.com/publisher-tag/guides/ad-sizes#fluid_ads
  useEffect(() => {
    if (isNativeTopAdRendered) {
      const nativeTopAd = document.getElementById('native_top_ad');
      const nativeTopAdObserver = new ResizeObserver(() => {
        setNativeTopAdHeight(nativeTopAd?.clientHeight || 0);
      });
      nativeTopAdObserver.observe(nativeTopAd!);

      return () => nativeTopAdObserver.unobserve(nativeTopAd!);
    }
  }, [isNativeTopAdRendered]);

  useEffect(() => {
    if (isNativeMiddleAdRendered) {
      const nativeMiddleAd = document.getElementById('native_middle_ad');
      const nativeMiddleAdObserver = new ResizeObserver(() => {
        setNativeMiddleAdHeight(nativeMiddleAd?.clientHeight || 0);
      });
      nativeMiddleAdObserver.observe(nativeMiddleAd!);

      return () => nativeMiddleAdObserver.unobserve(nativeMiddleAd!);
    }
  }, [isNativeMiddleAdRendered]);

  useEffect(() => {
    if (isNativeBottomAdRendered) {
      const nativeBottomAd = document.getElementById('native_bottom_ad');
      const nativeBottomAdObserver = new ResizeObserver(() => {
        setNativeBottomAdHeight(nativeBottomAd?.clientHeight || 0);
      });
      nativeBottomAdObserver.observe(nativeBottomAd!);

      return () => nativeBottomAdObserver.unobserve(nativeBottomAd!);
    }
  }, [isNativeBottomAdRendered]);

  return {
    topLeaderboardAdsHeight,
    middleLeaderboardAdsHeight,
    bottomLeaderboardAdsHeight,
    hasMrecOrTopStickyAds,
    topStickyAdHeight,
    nativeTopAdHeight,
    nativeMiddleAdHeight,
    nativeBottomAdHeight,
  };
};

export default useFlightSearchAds;
